import React, { useState, useEffect } from 'react';
import { X, Calendar, Search, User, Timer, Tag } from 'lucide-react';
import DateTimePicker from './DateTimePicker';
import jobService from '../services/jobService';
import StatusSelect from './StatusSelect';
import { getFranchiseeIdForCRUD, getFranchiseeIdForDataLoading } from '../App/services/appHelpers';

const getDefaultDuration = () => {
  try {
    const stored = localStorage.getItem('pestcrm_default_appointment_duration');
    if (stored) return parseInt(stored, 10);
  } catch (e) { /* ignore */ }
  return 60;
};

const AddAppointmentModal = ({ onClose, onSubmit, user, initialDateTime = null, initialData = null, editingAppointment = null }) => {
  // Extract initial values from initialData
  const initJobId = initialData?.jobId || '';
  const initDateTime = initialData?.initialDateTime || initialDateTime;
  const isEditing = !!editingAppointment;

  const getInitialFormData = () => {
    const defaultDuration = getDefaultDuration();
    if (editingAppointment) {
      const aptDate = new Date(editingAppointment.scheduled_date);
      return {
        job_id: editingAppointment.job_id || initJobId,
        scheduled_date: aptDate.toISOString().split('T')[0],
        scheduled_time: (() => {
          const h = String(aptDate.getHours()).padStart(2, '0');
          const m = String(Math.round(aptDate.getMinutes() / 5) * 5).padStart(2, '0');
          return m === '60' ? `${String(Number(h) + 1).padStart(2, '0')}:00` : `${h}:${m}`;
        })(),
        estimated_duration: editingAppointment.estimated_duration || defaultDuration,
        appointment_type: editingAppointment.appointment_type || 'first_visit',
        status: editingAppointment.status || 'scheduled',
        assigned_technician_id: editingAppointment.assigned_technician_id || '',
        notes: editingAppointment.notes || ''
      };
    }
    return {
      job_id: initJobId,
      scheduled_date: initDateTime ? initDateTime.toISOString().split('T')[0] : '',
      scheduled_time: initDateTime ? `${String(initDateTime.getHours()).padStart(2, '0')}:00` : '',
      estimated_duration: defaultDuration,
      appointment_type: 'first_visit',
      status: 'scheduled',
      assigned_technician_id: '',
      notes: ''
    };
  };

  const [formData, setFormData] = useState(getInitialFormData);

  const [isJobPreselected] = useState(!!initJobId || isEditing); // Track if job was pre-selected

  const [errors, setErrors] = useState({});
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [showJobSelector, setShowJobSelector] = useState(false);
  const [jobSearchTerm, setJobSearchTerm] = useState('');

  // Status options
  const statusOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Appointment types
  const appointmentTypes = [
    { value: 'first_visit', label: 'First Visit' },
    { value: 'follow_up', label: 'Follow-up' },
    { value: 'inspection', label: 'Inspection' },
    { value: 'treatment', label: 'Treatment' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'final_visit', label: 'Final Visit' }
  ];

  // Duration options
  const durationOptions = [
    { value: 15, label: '15 Mins' },
    { value: 20, label: '20 Mins' },
    { value: 25, label: '25 Mins' },
    { value: 30, label: '30 Mins' },
    { value: 35, label: '35 Mins' },
    { value: 40, label: '40 Mins' },
    { value: 45, label: '45 Mins' },
    { value: 50, label: '50 Mins' },
    { value: 55, label: '55 Mins' },
    { value: 60, label: '1 Hour' },
    { value: 75, label: '1 Hour 15 Mins' },
    { value: 90, label: '1 Hour 30 Mins' },
    { value: 105, label: '1 Hour 45 Mins' },
    { value: 120, label: '2 Hours' },
    { value: 150, label: '2 Hours 30 Mins' },
    { value: 180, label: '3 Hours' },
    { value: 210, label: '3 Hours 30 Mins' },
    { value: 240, label: '4 Hours' },
    { value: 300, label: '5 Hours' },
    { value: 360, label: '6 Hours' },
    { value: 420, label: '7 Hours' },
    { value: 480, label: '8 Hours' }
  ];

  // Load jobs on mount
  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    if (!user) return;

    try {
      setLoadingJobs(true);
      const franchiseeId = getFranchiseeIdForDataLoading(user);

      const response = await jobService.getJobs(franchiseeId, {
        page: 1,
        limit: 500,
        status: 'active', // Only show active jobs
        sortBy: 'created_at',
        sortOrder: 'DESC'
      });

      setJobs(response.jobs || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.job_id) {
      newErrors.job_id = 'Job selection is required';
    }

    if (!formData.scheduled_date) {
      newErrors.scheduled_date = 'Date is required';
    }

    if (!formData.scheduled_time) {
      newErrors.scheduled_time = 'Time is required';
    }

    if (!formData.appointment_type) {
      newErrors.appointment_type = 'Appointment type is required';
    }

    if (!formData.estimated_duration || formData.estimated_duration < 15) {
      newErrors.estimated_duration = 'Duration must be at least 15 minutes';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        const scheduledDateTime = `${formData.scheduled_date}T${formData.scheduled_time}:00`;
        const duration = parseInt(formData.estimated_duration);
        const scheduledDate = new Date(scheduledDateTime);
        const scheduledEndDate = new Date(scheduledDate.getTime() + (duration * 60000));

        const submitData = {
          ...(isEditing && { id: editingAppointment.id }),
          job_id: formData.job_id,
          scheduled_date: scheduledDateTime,
          scheduled_end_date: scheduledEndDate.toISOString(),
          estimated_duration: duration,
          appointment_type: formData.appointment_type,
          status: formData.status,
          assigned_technician_id: formData.assigned_technician_id || null,
          notes: formData.notes
        };

        await onSubmit(submitData);

        onClose();
      } catch (error) {
        // Show error message
        alert('Failed to create appointment: ' + (error.message || 'Unknown error'));
      }
    }
  };

  const selectedJob = jobs.find(j => j.id === formData.job_id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
          <h2 className="text-2xl font-bold text-white">{isEditing ? 'Edit Appointment' : 'Schedule New Appointment'}</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors duration-200"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Job Selection - only show if job not pre-selected */}
            {!isJobPreselected && (
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-pest-blue">Job Selection</h3>
                </div>

                <div className="p-6">
                {loadingJobs && (
                  <div className="text-center py-4">
                    <p className="text-gray-600">Loading jobs...</p>
                  </div>
                )}

                {!selectedJob && !loadingJobs && !showJobSelector && (
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => setShowJobSelector(true)}
                      className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-pest-blue hover:bg-blue-50 transition-all text-gray-600 hover:text-pest-blue font-medium"
                    >
                      <Search className="inline w-5 h-5 mr-2" />
                      Select Job
                    </button>
                    {errors.job_id && <p className="text-red-500 text-sm">{errors.job_id}</p>}
                  </div>
                )}

                {!selectedJob && !loadingJobs && showJobSelector && (
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="job_search" className="block text-sm font-medium text-gray-700 mb-2">
                        Search Job *
                      </label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          id="job_search"
                          value={jobSearchTerm}
                          onChange={(e) => setJobSearchTerm(e.target.value)}
                          placeholder="Search by job title or customer..."
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div className="max-h-60 overflow-y-auto border-2 border-gray-200 rounded-lg">
                      {jobs
                        .filter(job => {
                          const searchLower = jobSearchTerm.toLowerCase();
                          return (
                            (job.category_name || '').toLowerCase().includes(searchLower) ||
                            (job.customer_name && job.customer_name.toLowerCase().includes(searchLower))
                          );
                        })
                        .map(job => (
                          <button
                            key={job.id}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, job_id: job.id }));
                              setShowJobSelector(false);
                              setJobSearchTerm('');
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                          >
                            <div className="font-medium text-gray-900">{job.category_name || 'Job'}</div>
                            {job.customer_name && (
                              <div className="text-sm text-gray-600">{job.customer_name}</div>
                            )}
                            <div className="text-xs text-gray-500">Status: {job.status}</div>
                          </button>
                        ))}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setShowJobSelector(false);
                        setJobSearchTerm('');
                      }}
                      className="w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                    >
                      Cancel
                    </button>
                    {errors.job_id && <p className="text-red-500 text-sm">{errors.job_id}</p>}
                  </div>
                )}

                {selectedJob && (
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center">
                        <User className="w-5 h-5 text-blue-600 mr-2" />
                        <h4 className="text-sm font-semibold text-blue-900">Selected Job</h4>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, job_id: '' }));
                          setShowJobSelector(true);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                      >
                        Change
                      </button>
                    </div>
                    <div className="text-sm text-blue-900 space-y-1 ml-7">
                      <p className="font-semibold">{selectedJob.category_name || 'Job'}</p>
                      {selectedJob.customer_name && (
                        <p className="text-blue-700">Customer: {selectedJob.customer_name}</p>
                      )}
                      <p className="text-blue-700">Status: {selectedJob.status}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Appointment Details */}
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-pest-blue">Appointment Details</h3>
              </div>

              <div className="p-6 space-y-4">
                {/* Date and Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar className="inline w-4 h-4 mr-2" />
                    Date & Time *
                  </label>
                  <DateTimePicker
                    value={formData.scheduled_date && formData.scheduled_time ? `${formData.scheduled_date}T${formData.scheduled_time}` : formData.scheduled_date || ''}
                    onChange={(val) => {
                      if (!val) {
                        handleChange({ target: { name: 'scheduled_date', value: '' } });
                        handleChange({ target: { name: 'scheduled_time', value: '' } });
                      } else {
                        const [d, t] = val.split('T');
                        handleChange({ target: { name: 'scheduled_date', value: d || '' } });
                        handleChange({ target: { name: 'scheduled_time', value: t || '' } });
                      }
                    }}
                    className="w-full"
                  />
                  {(errors.scheduled_date || errors.scheduled_time) && (
                    <p className="text-red-500 text-sm mt-2">{errors.scheduled_date || errors.scheduled_time}</p>
                  )}
                </div>

                {/* Duration and Type */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="estimated_duration" className="block text-sm font-medium text-gray-700 mb-2">
                      <Timer className="inline w-4 h-4 mr-2" />
                      Duration *
                    </label>
                    <select
                      id="estimated_duration"
                      name="estimated_duration"
                      value={formData.estimated_duration}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors cursor-pointer ${
                        errors.estimated_duration ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {durationOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {errors.estimated_duration && <p className="text-red-500 text-sm mt-2">{errors.estimated_duration}</p>}
                  </div>

                  <div>
                    <label htmlFor="appointment_type" className="block text-sm font-medium text-gray-700 mb-2">
                      <Tag className="inline w-4 h-4 mr-2" />
                      Appointment Type *
                    </label>
                    <select
                      id="appointment_type"
                      name="appointment_type"
                      value={formData.appointment_type}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors cursor-pointer ${
                        errors.appointment_type ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {appointmentTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                    {errors.appointment_type && <p className="text-red-500 text-sm mt-2">{errors.appointment_type}</p>}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue hover:border-gray-400 transition-colors resize-none"
                    placeholder="Any special instructions or notes for this appointment..."
                  />
                </div>

              </div>
            </div>

          </form>
        </div>

        {/* Footer - outside scroll area so dropdown extends upward */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-end justify-between overflow-visible">
          <div>
            <span className="block text-sm font-semibold text-gray-700 mb-1.5">Status</span>
            <StatusSelect
              value={formData.status}
              onChange={(val) => handleChange({ target: { name: 'status', value: val } })}
              options={statusOptions}
              size="lg"
              dropUp
            />
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            className="btn btn-lg btn-primary"
          >
            {isEditing ? 'Update Appointment' : 'Schedule Appointment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAppointmentModal;
